import React, { JSX, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router'
import classNames from 'classnames'
import {
  PlusIcon,
  VideoCameraIcon,
  TrashIcon,
  PencilSquareIcon,
  EyeIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline'
import { PlayCircleIcon, StopCircleIcon } from '@heroicons/react/24/solid'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { useEventDetail } from 'hooks/useEventDetail'
import { useDefaultOverlayLink, buildCamControlShortUrl } from 'hooks/useOverlayLinks'
import { graphqlRequest } from 'lib/helpers'
import { eventSectionPath } from 'lib/paths'
import { sortByName } from 'lib/elementOrder'
import {
  CameraCommandInput,
  CameraControlStatus,
  CameraControlSummary,
  ChannelBinding,
  DeletionResult,
  EventDetail
} from 'types/api'
import createCameraControlMutation from 'graphql/createCameraControl.graphql'
import updateCameraControlMutation from 'graphql/updateCameraControl.graphql'
import deleteCameraControlMutation from 'graphql/deleteCameraControl.graphql'
import startCameraControlMutation from 'graphql/startCameraControl.graphql'
import stopCameraControlMutation from 'graphql/stopCameraControl.graphql'

const MIN_COMMANDS = 2
const MAX_COMMANDS = 6
const COMMAND_MIN_LEN = 2
const COMMAND_MAX_LEN = 16
const MIN_CHANGE_LIMIT = 1
const MAX_CHANGE_LIMIT = 10000
const DEFAULT_CHANGE_LIMIT = 10
const MAX_COOLDOWN_MS = 300_000
const DEFAULT_COOLDOWN_MS = 5000

const DEFAULT_COMMANDS = ['!up', '!down']

const clampCooldownMs = (ms: number): number =>
  Math.min(MAX_COOLDOWN_MS, Math.max(0, Math.round(ms)))

export const formatCooldownSeconds = (ms: number): string => (ms / 1000).toFixed(3)

export const parseCooldownSeconds = (text: string): number | 'invalid' => {
  const trimmed = text.trim()
  if (trimmed === '') return 0
  const seconds = Number(trimmed)
  if (!Number.isFinite(seconds) || seconds < 0 || Object.is(seconds, -0)) return 'invalid'
  return clampCooldownMs(seconds * 1000)
}

export const counterModeOf = (raw: string): 'ROUNDS' | 'ACCUMULATE' =>
  raw === 'ACCUMULATE' ? 'ACCUMULATE' : 'ROUNDS'

const STATUS_PILL: Record<CameraControlStatus, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30',
  INACTIVE: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30'
}

const STATUS_LABEL: Record<CameraControlStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive'
}

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const FieldLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

const inputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'

const CopyButton = ({ value, label }: { value: string, label: string }): JSX.Element => {
  const [copied, setCopied] = useState(false)
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      globalThis.setTimeout(() => setCopied(false), 1500)
    } catch {
    }
  }
  return (
    <button
      type='button'
      onClick={() => { void handleCopy() }}
      title={`Copy ${label}`}
      className='inline-flex items-center text-dm-text-accent hover:text-dm-text-secondary'
    >
      {copied
        ? <ClipboardDocumentCheckIcon className='size-4 text-emerald-400' aria-hidden='true' />
        : <ClipboardDocumentIcon className='size-4' aria-hidden='true' />}
      <span className='sr-only'>Copy {label}</span>
    </button>
  )
}

const CooldownSecondsInput = ({
  valueMs,
  onCommit
}: {
  valueMs: number
  onCommit: (ms: number) => void
}): JSX.Element => {
  const [text, setText] = useState(formatCooldownSeconds(valueMs))
  const [badInput, setBadInput] = useState(false)
  const invalid = badInput || parseCooldownSeconds(text) === 'invalid'
  return (
    <div data-testid='camctl-cooldown-block'>
      <FieldLabel>Cooldown between changes (seconds)</FieldLabel>
      <input
        type='number'
        inputMode='decimal'
        min={0}
        max={MAX_COOLDOWN_MS / 1000}
        step={0.001}
        value={text}
        aria-label='Cooldown between displayed changes in seconds'
        data-testid='camctl-cooldown-input'
        onChange={(e) => {
          const next = e.target.value
          const bad = e.target.validity.badInput
          setText(next)
          setBadInput(bad)
          if (bad) return
          const ms = parseCooldownSeconds(next)
          if (ms !== 'invalid') onCommit(ms)
        }}
        onBlur={(e) => {
          const formatted = formatCooldownSeconds(valueMs)
          setText(formatted)
          setBadInput(false)
          if (e.target.value !== formatted) e.target.value = formatted
        }}
        className={inputClass}
      />
      {invalid && (
        <p className='mt-1 text-[11px] text-amber-300'>
          Must be 0 to 300 seconds the previous cooldown is kept until this is fixed.
        </p>
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent'>
        Two wins arriving close together display this many seconds apart
        queued, never rapid-flashed. Stored to the millisecond.
      </p>
    </div>
  )
}

interface CamCtlDraft {
  name: string
  commands: string[]
  changeLimit: number
  cooldownMs: number
  counterMode: 'ROUNDS' | 'ACCUMULATE'
}

const draftFromCamCtl = (c: CameraControlSummary): CamCtlDraft => ({
  name: c.name,
  commands: c.commands.map((cmd) => cmd.command),
  changeLimit: c.changeLimit,
  cooldownMs: c.cooldownMs,
  counterMode: counterModeOf(c.counterMode)
})

const emptyDraft = (): CamCtlDraft => ({
  name: '',
  commands: [...DEFAULT_COMMANDS],
  changeLimit: DEFAULT_CHANGE_LIMIT,
  cooldownMs: DEFAULT_COOLDOWN_MS,
  counterMode: 'ROUNDS'
})

const COUNTER_MODES: Array<{ mode: 'ROUNDS' | 'ACCUMULATE', label: string, blurb: string }> = [
  {
    mode: 'ROUNDS',
    label: 'Rounds',
    blurb: 'A win resets EVERY counter, each round is a fresh race. One counted message per chatter per round.'
  },
  {
    mode: 'ACCUMULATE',
    label: 'Accumulate',
    blurb: 'Losers keep their progress; only the winner resets after its win shows. Every message counts, no per-chatter dedupe.'
  }
]

const CameraControlForm = ({
  event,
  existing,
  overlayUrl,
  onSaved,
  onUpserted,
  onCancel
}: {
  event: EventDetail
  existing: CameraControlSummary | null
  overlayUrl: string | null
  onSaved: (camControl: CameraControlSummary) => void
  onUpserted: (camControl: CameraControlSummary) => void
  onCancel: () => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const { account } = useEntitlements()
  const [draft, setDraft] = useState<CamCtlDraft>(() =>
    existing !== null ? draftFromCamCtl(existing) : emptyDraft()
  )
  const [broadcasterUserIds, setBroadcasterUserIds] = useState<string[]>(() =>
    existing !== null ? existing.broadcasterUserIds : event.channels.map((c) => c.broadcasterUserId)
  )
  const youtubeInvited = account?.youtubeChatInvited === true
  const [youtubeVotesEnabled, setYoutubeVotesEnabled] = useState<boolean>(() =>
    existing !== null ? existing.youtubeVotesEnabled : false
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [liveBusy, setLiveBusy] = useState<'start' | 'stop' | null>(null)
  const [liveError, setLiveError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft, broadcasterUserIds, youtubeVotesEnabled])

  const isEdit = existing !== null
  const readOnly = event.status === 'ENDED'
  const canAddCommand = draft.commands.length < MAX_COMMANDS
  const canRemoveCommand = draft.commands.length > MIN_COMMANDS

  const updateCommand = (idx: number, value: string): void => {
    setDraft((d) => ({ ...d, commands: d.commands.map((c, i) => i === idx ? value : c) }))
  }

  const addCommand = (): void => {
    if (!canAddCommand) return
    setDraft((d) => ({ ...d, commands: [...d.commands, ''] }))
  }

  const removeCommand = (idx: number): void => {
    if (!canRemoveCommand) return
    setDraft((d) => ({ ...d, commands: d.commands.filter((_, i) => i !== idx) }))
  }

  const toggleChannel = (broadcasterUserId: string): void => {
    setBroadcasterUserIds((prev) => prev.includes(broadcasterUserId)
      ? prev.filter((id) => id !== broadcasterUserId)
      : [...prev, broadcasterUserId])
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    if (draft.name.trim().length === 0) {
      setError('Name is required.')
      return
    }
    const commands = draft.commands.map((c) => c.trim())
    if (commands.some((c) => c.length === 0)) {
      setError('Every command needs text.')
      return
    }
    if (commands.some((c) => c.length < COMMAND_MIN_LEN || c.length > COMMAND_MAX_LEN)) {
      setError(`Commands must be ${COMMAND_MIN_LEN}–${COMMAND_MAX_LEN} characters.`)
      return
    }
    if (commands.some((c) => /\s/.test(c))) {
      setError('Commands must be a single word no spaces.')
      return
    }
    const lowered = commands.map((c) => c.toLowerCase())
    if (new Set(lowered).size !== lowered.length) {
      setError('Commands must be unique (case-insensitive !up and !UP are the same command).')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const commandsInput: CameraCommandInput[] = commands.map((command) => ({ command }))
      const sharedVars = {
        name: draft.name.trim(),
        broadcasterUserIds,
        commands: commandsInput,
        changeLimit: draft.changeLimit,
        cooldownMs: draft.cooldownMs,
        counterMode: draft.counterMode,
        youtubeVotesEnabled
      }
      if (isEdit) {
        const { camControl } = await graphqlRequest<{ camControl: CameraControlSummary }>(
          updateCameraControlMutation,
          { camControlId: existing.camControlId, ...sharedVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(camControl)
      } else {
        const { camControl } = await graphqlRequest<{ camControl: CameraControlSummary }>(
          createCameraControlMutation,
          { eventId: event.eventId, ...sharedVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(camControl)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save camera control')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStart = async (): Promise<void> => {
    if (auth0Token === undefined || existing === null || liveBusy !== null || readOnly) return
    setLiveBusy('start')
    setLiveError(null)
    try {
      const { camControl } = await graphqlRequest<{ camControl: Pick<CameraControlSummary, 'camControlId' | 'status' | 'startedAt' | 'stoppedAt' | 'version' | 'currentCommandIndex' | 'streak'> }>(
        startCameraControlMutation, { camControlId: existing.camControlId }, auth0Token
      )
      onUpserted({ ...existing, ...camControl })
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Failed to start camera control')
    } finally {
      setLiveBusy(null)
    }
  }

  const handleStop = async (): Promise<void> => {
    if (auth0Token === undefined || existing === null || liveBusy !== null || readOnly) return
    setLiveBusy('stop')
    setLiveError(null)
    try {
      const { camControl } = await graphqlRequest<{ camControl: Pick<CameraControlSummary, 'camControlId' | 'status' | 'startedAt' | 'stoppedAt' | 'version' | 'currentCommandIndex' | 'streak'> }>(
        stopCameraControlMutation, { camControlId: existing.camControlId }, auth0Token
      )
      onUpserted({ ...existing, ...camControl })
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Failed to stop camera control')
    } finally {
      setLiveBusy(null)
    }
  }

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset disabled={readOnly} className='contents space-y-1.5'>
          <SectionCard
            title={readOnly ? 'View camera control' : isEdit ? 'Edit camera control' : 'New camera control'}
            action={
            readOnly
              ? (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
                  <EyeIcon className='size-3' aria-hidden='true' />
                  Read-only
                </span>
                )
              : (
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to camera controls
                </button>
                )
          }
          >
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Short label for this camera control (shown in the list)'
                required
                className={inputClass}
              />
            </label>

            <div className='mb-4'>
              <FieldLabel>
                <span className='flex items-center gap-1.5'>
                  <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                  Twitch channels
                </span>
              </FieldLabel>
              {event.channels.length === 0
                ? (
                  <p className='rounded-md border border-dashed border-white/10 p-3 text-xs text-dm-text-secondary'>
                    No channels bound to this stream yet. Add one on the Integrations page.
                  </p>
                  )
                : (
                  <ul className='space-y-1.5'>
                    {event.channels.map((c: ChannelBinding) => (
                      <li key={c.broadcasterUserId}>
                        <label className='flex cursor-pointer items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm hover:bg-dm-bg-accent/50'>
                          <input
                            type='checkbox'
                            checked={broadcasterUserIds.includes(c.broadcasterUserId)}
                            onChange={() => toggleChannel(c.broadcasterUserId)}
                            className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                          />
                          <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                          <span className='text-dm-text-primary'>{c.channelLogin}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  )}
            </div>

            {youtubeInvited && (
              <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
                <input
                  type='checkbox'
                  checked={youtubeVotesEnabled}
                  onChange={(e) => setYoutubeVotesEnabled(e.target.checked)}
                  className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                />
                Count YouTube commands (this stream&apos;s YouTube sources)
              </label>
            )}
          </SectionCard>

          <SectionCard
            title='Chat commands'
            action={
              <button
                type='button'
                onClick={addCommand}
                disabled={!canAddCommand}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add command
              </button>
          }
          >
            <ul className='space-y-2'>
              {draft.commands.map((cmd, i) => (
                <li key={i} className='flex items-center gap-2'>
                  <input
                    value={cmd}
                    onChange={(e) => updateCommand(i, e.target.value)}
                    placeholder={`!command${i + 1}`}
                    required
                    maxLength={COMMAND_MAX_LEN}
                    aria-label={`Command ${i + 1}`}
                    className='w-full rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 font-mono text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                  />
                  <button
                    type='button'
                    onClick={() => removeCommand(i)}
                    disabled={!canRemoveCommand}
                    title='Remove this command'
                    className='inline-flex shrink-0 items-center gap-1 rounded-md border border-white/10 px-2 py-2 text-[11px] text-dm-text-secondary hover:border-red-500/40 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    <TrashIcon className='size-3.5' aria-hidden='true' />
                    <span className='sr-only'>Remove command {i + 1}</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className='mt-3 text-[11px] text-dm-text-accent'>
              {MIN_COMMANDS}–{MAX_COMMANDS} preset commands, each a single word of {COMMAND_MIN_LEN}–{COMMAND_MAX_LEN} characters
              (any prefix or none — “!up”, “#up” and “up” are all fine), unique ignoring case.
              A chat message counts only when the WHOLE trimmed message matches a command
              (case-insensitive) “!up” and “ !UP ” count, “go !up now” does not.
            </p>
          </SectionCard>

          <SectionCard title='Thresholds & timing'>
            <label className='mb-4 block'>
              <FieldLabel>Change limit</FieldLabel>
              <NumberInput
                value={draft.changeLimit}
                min={MIN_CHANGE_LIMIT}
                max={MAX_CHANGE_LIMIT}
                onCommit={(n) => setDraft((d) => ({ ...d, changeLimit: n }))}
                data-testid='camctl-change-limit-input'
                className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Chat sightings a command needs before it wins and becomes the current instruction.
              </p>
            </label>
            <div className='mb-4'>
              <CooldownSecondsInput
                valueMs={draft.cooldownMs}
                onCommit={(ms) => setDraft((d) => ({ ...d, cooldownMs: ms }))}
              />
            </div>
            <div>
              <FieldLabel>Counter mode</FieldLabel>
              <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
                {COUNTER_MODES.map(({ mode, label, blurb }) => (
                  <button
                    key={mode}
                    type='button'
                    onClick={() => setDraft((d) => ({ ...d, counterMode: mode }))}
                    aria-pressed={draft.counterMode === mode}
                    data-testid={`camctl-mode-${mode.toLowerCase()}`}
                    className={classNames(
                      'rounded-md border px-3 py-2 text-left',
                      draft.counterMode === mode
                        ? 'border-brand/60 bg-brand/10'
                        : 'border-white/10 bg-dm-bg-accent/30 hover:bg-dm-bg-accent/50'
                    )}
                  >
                    <span className='block text-sm font-medium text-dm-text-primary'>{label}</span>
                    <span className='mt-0.5 block text-[11px] leading-relaxed text-dm-text-accent'>{blurb}</span>
                  </button>
                ))}
              </div>
            </div>
          </SectionCard>
        </fieldset>
      </div>

      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          {readOnly
            ? (
              <>
                <div className='rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
                  This stream has ended, so its camera controls are read-only.
                </div>
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to camera controls
                </button>
              </>
              )
            : (
              <div className='flex items-center gap-2'>
                <button
                  type='submit'
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {submitting && <LoadingSpinner />}
                  {isEdit ? 'Save changes' : 'Create camera control'}
                </button>
                <button
                  type='button'
                  onClick={onCancel}
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
                >
                  Cancel
                </button>
              </div>
              )}
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='camctl-saved' />}

          {isEdit && existing !== null && (
            <div className='rounded-xl border border-white/5 bg-dm-bg-secondary p-4'>
              <div className='mb-3 flex items-center justify-between'>
                <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>Live control</h4>
                <span className={classNames(
                  'rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
                  STATUS_PILL[existing.status]
                )}
                >
                  {STATUS_LABEL[existing.status]}
                </span>
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <button
                  type='button'
                  onClick={() => { void handleStart() }}
                  disabled={liveBusy !== null || existing.status === 'ACTIVE' || readOnly}
                  data-testid='camctl-form-start'
                  className='inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {liveBusy === 'start' ? <LoadingSpinner /> : <PlayCircleIcon className='size-4' aria-hidden='true' />}
                  Start
                </button>
                <button
                  type='button'
                  onClick={() => { void handleStop() }}
                  disabled={liveBusy !== null || existing.status !== 'ACTIVE' || readOnly}
                  data-testid='camctl-form-stop'
                  className='inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-400 px-3 py-2 text-xs font-semibold text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {liveBusy === 'stop' ? <LoadingSpinner /> : <StopCircleIcon className='size-4' aria-hidden='true' />}
                  Stop
                </button>
              </div>
              {liveError !== null && (
                <div className='mt-2'>
                  <ErrorInline onDismiss={() => setLiveError(null)}>{liveError}</ErrorInline>
                </div>
              )}
              {overlayUrl !== null && (
                <div className='mt-3 flex items-center gap-1.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
                  <span className='min-w-0 flex-1 truncate text-[11px] text-dm-text-secondary'>{overlayUrl}</span>
                  <CopyButton value={overlayUrl} label='cam-op page URL' />
                  <a
                    href={overlayUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    title='Open the cam-op page in a new window'
                    className='inline-flex items-center text-dm-text-accent hover:text-dm-text-secondary'
                  >
                    <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
                    <span className='sr-only'>Open cam-op page</span>
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </form>
  )
}

const CamCtlListRow = ({
  camControl,
  overlayUrl,
  onEdit,
  onDelete,
  busy,
  readOnly
}: {
  camControl: CameraControlSummary
  overlayUrl: string | null
  onEdit: () => void
  onDelete: () => void
  busy: boolean
  readOnly: boolean
}): JSX.Element => {
  const isActive = camControl.status === 'ACTIVE'
  return (
    <li
      data-testid={`camctl-row-${camControl.camControlId}`}
      className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className={classNames(
            'shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
            STATUS_PILL[camControl.status]
          )}
          >
            {STATUS_LABEL[camControl.status]}
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>
              {camControl.name}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {camControl.commands.map((c) => c.command).join(' ')} · limit {camControl.changeLimit} ·{' '}
              {formatCooldownSeconds(camControl.cooldownMs)}s cooldown · {counterModeOf(camControl.counterMode) === 'ROUNDS' ? 'rounds' : 'accumulate'}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          {overlayUrl !== null && (
            <>
              <CopyButton value={overlayUrl} label={`cam-op URL for ${camControl.name}`} />
              <a
                href={overlayUrl}
                target='_blank'
                rel='noopener noreferrer'
                title='Open the cam-op page in a new window'
                className='inline-flex items-center text-dm-text-accent hover:text-dm-text-secondary'
              >
                <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
                <span className='sr-only'>Open cam-op page for {camControl.name}</span>
              </a>
            </>
          )}
          <button
            type='button'
            onClick={onEdit}
            title={readOnly ? 'View' : 'Edit'}
            disabled={busy}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {readOnly
              ? <EyeIcon className='size-3.5' aria-hidden='true' />
              : <PencilSquareIcon className='size-3.5' aria-hidden='true' />}
            {readOnly ? 'View' : 'Edit'}
          </button>
          {!readOnly && (
            <button
              type='button'
              onClick={onDelete}
              disabled={busy || isActive}
              title={isActive ? 'Stop this camera control first (on its form or in the overlay editor)' : 'Delete'}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busy ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
              Delete
            </button>
          )}
        </div>
      </div>
    </li>
  )
}

type Mode =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit', camControlId: string }

export const CameraControlsPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const { staffUncapped } = useEntitlements()
  const { event, setEvent, refetchEvent } = useEventDetail()
  const { k: overlayLinkK } = useDefaultOverlayLink(event.eventId)
  const isEnded = event.status === 'ENDED'
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [rowBusyId, setRowBusyId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const editing = useMemo<CameraControlSummary | null>(() => {
    if (mode.kind !== 'edit') return null
    return event.cameraControls.find((c) => c.camControlId === mode.camControlId) ?? null
  }, [mode, event.cameraControls])

  const camOpUrlFor = (camControlId: string): string | null =>
    buildCamControlShortUrl(event.slug, camControlId, overlayLinkK)

  const upsertCamCtl = (camControl: CameraControlSummary): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          cameraControls: prev.cameraControls.some((c) => c.camControlId === camControl.camControlId)
            ? prev.cameraControls.map((c) => c.camControlId === camControl.camControlId ? camControl : c)
            : [...prev.cameraControls, camControl]
        }))
  }

  const removeCamCtl = (camControlId: string): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          cameraControls: prev.cameraControls.filter((c) => c.camControlId !== camControlId)
        }))
  }

  const handleDelete = async (camControl: CameraControlSummary): Promise<void> => {
    if (auth0Token === undefined || rowBusyId !== null) return
    const ok = globalThis.confirm(
      `Delete camera control "${camControl.name}"?\n\nIts counters, queue and cam-op page go with it. This cannot be undone.`
    )
    if (!ok) return
    setRowBusyId(camControl.camControlId)
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteCameraControlMutation, { camControlId: camControl.camControlId }, auth0Token
      )
      if (result.deleted) removeCamCtl(camControl.camControlId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete camera control')
    } finally {
      setRowBusyId(null)
    }
  }

  if (!staffUncapped) {
    return <Navigate to={eventSectionPath(event.eventId, 'overview')} replace />
  }

  if (mode.kind === 'create') {
    return (
      <CameraControlForm
        event={event}
        existing={null}
        overlayUrl={null}
        onSaved={(camControl) => { upsertCamCtl(camControl); setMode({ kind: 'edit', camControlId: camControl.camControlId }) }}
        onUpserted={upsertCamCtl}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  if (mode.kind === 'edit') {
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That camera control no longer exists. Returning to the list…</ErrorInline>
          <button onClick={() => setMode({ kind: 'list' })} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            Back to camera controls
          </button>
        </div>
      )
    }
    return (
      <CameraControlForm
        event={event}
        existing={editing}
        overlayUrl={camOpUrlFor(editing.camControlId)}
        onSaved={upsertCamCtl}
        onUpserted={upsertCamCtl}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Camera Controls'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void refetchEvent() }}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              <ArrowPathIcon className='size-3.5' aria-hidden='true' />
              Refresh
            </button>
            {!isEnded && (
              <button
                type='button'
                data-testid='add-camctl-button'
                onClick={() => setMode({ kind: 'create' })}
                className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add camera control
              </button>
            )}
          </div>
        }
      >
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {event.cameraControls.length === 0
          ? (
            <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
              <VideoCameraIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
              <p>{isEnded ? 'This stream ended with no camera controls.' : 'No camera controls yet for this stream.'}</p>
              {!isEnded && (
                <button
                  type='button'
                  onClick={() => setMode({ kind: 'create' })}
                  className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
                >
                  <PlusIcon className='size-3.5' aria-hidden='true' />
                  Create the first one
                </button>
              )}
            </div>
            )
          : (
            <ul className='space-y-2' data-testid='camctls-list'>
              {sortByName(event.cameraControls, (c) => c.name, (c) => c.camControlId).map((camControl: CameraControlSummary) => (
                <CamCtlListRow
                  key={camControl.camControlId}
                  camControl={camControl}
                  overlayUrl={camOpUrlFor(camControl.camControlId)}
                  onEdit={() => setMode({ kind: 'edit', camControlId: camControl.camControlId })}
                  onDelete={() => { void handleDelete(camControl) }}
                  busy={rowBusyId === camControl.camControlId}
                  readOnly={isEnded}
                />
              ))}
            </ul>
            )}
      </SectionCard>
    </div>
  )
}
